import React, { useState, useEffect, useRef } from 'react';

interface BettingWheelProps {
  onAmountChange: (amount: number) => void;
  minBet: number;
  maxBet: number;
  currentBet?: number;
  increment?: number;
}

const BettingWheel: React.FC<BettingWheelProps> = ({
  onAmountChange,
  minBet,
  maxBet,
  currentBet = 0,
  increment = 10
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const wheelRef = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<NodeJS.Timeout>();
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Generate betting values with progressive increments (at least 10% of current value)
  const values: number[] = [];
  let currentValue = minBet;
  values.push(currentValue);
  
  while (currentValue < maxBet) {
    // Calculate next increment: max of base increment or 10% of current value
    const minIncrement = Math.max(increment, Math.ceil(currentValue * 0.1));
    // Round to nearest 10 for cleaner values
    const roundedIncrement = Math.ceil(minIncrement / 10) * 10;
    
    currentValue += roundedIncrement;
    
    if (currentValue <= maxBet) {
      values.push(currentValue);
    } else {
      // Always include max bet as last option if not already there
      if (values[values.length - 1] !== maxBet) {
        values.push(maxBet);
      }
      break;
    }
  }

  const selectedAmount = values[selectedIndex] || minBet;

  useEffect(() => {
    onAmountChange(selectedAmount);
  }, [selectedAmount, onAmountChange]);

  const handleScroll = (direction: 'up' | 'down') => {
    if (direction === 'up' && selectedIndex < values.length - 1) {
      setSelectedIndex(prev => prev + 1);
    } else if (direction === 'down' && selectedIndex > 0) {
      setSelectedIndex(prev => prev - 1);
    }

    // Visual feedback
    setIsScrolling(true);
    clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => setIsScrolling(false), 150);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleScroll('up');
    } else if (e.deltaY > 0) {
      handleScroll('down');
    }
  };

  // Touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isUpSwipe = distance > 20;  // Reduced threshold for easier detection
    const isDownSwipe = distance < -20;

    if (isUpSwipe) {
      handleScroll('up');
    } else if (isDownSwipe) {
      handleScroll('down');
    }
    
    // Reset touch states
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Calculate visible range (show 5 values at a time)
  const visibleRange = 5;
  const halfRange = Math.floor(visibleRange / 2);

  // Check if we're on mobile (simple check using window width)
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  
  return (
    <div className={`bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg border border-gray-700 ${
      isMobile ? 'p-3 w-64' : 'p-2 w-48'
    }`}>
      {/* Title on top */}
      <div className={`text-gray-400 font-semibold uppercase tracking-wider text-center mb-2 ${
        isMobile ? 'text-sm' : 'text-xs'
      }`}>
        Bet Amount
      </div>

      <div className={`flex items-center justify-center ${isMobile ? 'gap-3' : 'gap-2'}`}>
        {/* Side view wheel */}
        <div className={`relative ${isMobile ? 'h-28 w-16' : 'h-20 w-12'}`}>
          {/* Wheel frame/border */}
          <div className="absolute inset-0 rounded-full border-2 border-gray-600 bg-gray-800/50" 
               style={{
                 transform: 'rotateY(85deg)',
                 transformStyle: 'preserve-3d'
               }}
          />
          
          {/* Gradient overlays for depth */}
          <div 
            className="absolute inset-0 rounded-lg"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.6) 100%)',
              pointerEvents: 'none',
              zIndex: 10
            }}
          />
          
          {/* Selection indicator - centered */}
          <div className="absolute top-1/2 left-0 right-0 h-8 -translate-y-1/2 z-20">
            <div className="h-full border-t-2 border-b-2 border-yellow-400 bg-yellow-400/20 shadow-lg shadow-yellow-400/30" />
            <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-0 h-0 
                            border-t-4 border-b-4 border-r-4 
                            border-transparent border-r-yellow-400" />
            <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-0 h-0 
                            border-t-4 border-b-4 border-l-4 
                            border-transparent border-l-yellow-400" />
          </div>
          
          {/* Scrollable wheel content */}
          <div 
            ref={wheelRef}
            className="relative h-full w-full flex flex-col items-center justify-center cursor-ns-resize overflow-hidden touch-manipulation select-none"
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Render all values with proper positioning */}
            {values.map((value, index) => {
              const offset = index - selectedIndex;
              const distance = Math.abs(offset);
              const isVisible = distance <= halfRange;
              
              if (!isVisible) return null;
              
              const yPosition = offset * (isMobile ? 22 : 18); // More spacing on mobile
              const scale = 1 - (distance * 0.2);
              const opacity = 1 - (distance * 0.3);
              const rotateX = offset * 12; // Tilt for 3D effect
              
              return (
                <div
                  key={value}
                  className={`absolute flex items-center justify-center transition-all ${
                    isScrolling ? 'duration-75' : 'duration-300'
                  } ${
                    distance === 0 
                      ? `text-yellow-400 font-bold ${isMobile ? 'text-base' : 'text-sm'}` 
                      : distance === 1 
                        ? `text-gray-300 ${isMobile ? 'text-sm' : 'text-xs'}`
                        : `text-gray-500 ${isMobile ? 'text-sm' : 'text-xs'}`
                  }`}
                  style={{
                    transform: `
                      translateY(${yPosition}px) 
                      translateZ(${distance === 0 ? 10 : -distance * 5}px)
                      scale(${scale}) 
                      rotateX(${-rotateX}deg)
                    `,
                    opacity,
                    transformStyle: 'preserve-3d'
                  }}
                >
                  ${value}
                </div>
              );
            })}
          </div>
        </div>

        {/* Control buttons */}
        <div className="flex flex-col gap-1">
          <button
            onClick={() => handleScroll('up')}
            disabled={selectedIndex >= values.length - 1}
            className={`flex items-center justify-center bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50 rounded transition-colors ${
              isMobile ? 'w-8 h-8' : 'w-5 h-5'
            }`}
          >
            <svg className={`text-gray-300 ${isMobile ? 'w-4 h-4' : 'w-2 h-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          
          <div className="text-center text-xs text-gray-500 py-1">
            <div className={isMobile ? 'text-sm' : 'text-xs'}>{selectedIndex + 1}/{values.length}</div>
          </div>
          
          <button
            onClick={() => handleScroll('down')}
            disabled={selectedIndex <= 0}
            className={`flex items-center justify-center bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50 rounded transition-colors ${
              isMobile ? 'w-8 h-8' : 'w-5 h-5'
            }`}
          >
            <svg className={`text-gray-300 ${isMobile ? 'w-4 h-4' : 'w-2 h-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Quick select buttons */}
        <div className="flex flex-col gap-1">
          <button
            onClick={() => {
              const targetAmount = Math.min(selectedAmount * 2, maxBet);
              // Find closest value in array
              let closestIndex = 0;
              let closestDiff = Math.abs(values[0] - targetAmount);
              for (let i = 1; i < values.length; i++) {
                const diff = Math.abs(values[i] - targetAmount);
                if (diff < closestDiff) {
                  closestDiff = diff;
                  closestIndex = i;
                }
              }
              setSelectedIndex(closestIndex);
            }}
            className={`bg-gray-700 hover:bg-gray-600 rounded text-gray-300 transition-colors flex items-center justify-center ${
              isMobile ? 'w-10 h-6 text-sm' : 'w-6 h-4 text-xs'
            }`}
          >
            2x
          </button>
          <button
            onClick={() => {
              const targetAmount = Math.max(Math.floor(selectedAmount / 2), minBet);
              // Find closest value in array
              let closestIndex = 0;
              let closestDiff = Math.abs(values[0] - targetAmount);
              for (let i = 1; i < values.length; i++) {
                const diff = Math.abs(values[i] - targetAmount);
                if (diff < closestDiff) {
                  closestDiff = diff;
                  closestIndex = i;
                }
              }
              setSelectedIndex(closestIndex);
            }}
            className={`bg-gray-700 hover:bg-gray-600 rounded text-gray-300 transition-colors flex items-center justify-center ${
              isMobile ? 'w-10 h-6 text-sm' : 'w-6 h-4 text-xs'
            }`}
          >
            ½
          </button>
          <button
            onClick={() => setSelectedIndex(values.length - 1)}
            className={`bg-red-700 hover:bg-red-600 rounded text-red-300 transition-colors flex items-center justify-center ${
              isMobile ? 'w-10 h-6 text-sm' : 'w-6 h-4 text-xs'
            }`}
          >
            MAX
          </button>
        </div>
      </div>
    </div>
  );
};

export default BettingWheel;